<template>
  <div class="company-logo-wrapper" :class="{ 'logo-small': size === 'small', 'logo-medium': size === 'medium', 'logo-large': size === 'large' }">
    <img
      v-if="logoUrl"
      :src="logoUrl"
      :alt="`${companyName} logo`"
      class="company-logo"
      :class="{ 'logo-failed': hasError }"
      @error="handleImageError"
      @load="handleImageLoad"
      loading="lazy"
    />
    <div v-else-if="showFallback" class="logo-fallback">
      <v-icon size="inherit" color="grey lighten-1">{{ fallbackIcon }}</v-icon>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CompanyLogo',
  
  props: {
    domain: {
      type: String,
      required: false,
      default: null
    },
    companyName: {
      type: String,
      required: false,
      default: ''
    },
    size: {
      type: String,
      default: 'medium',
      validator: value => ['small', 'medium', 'large'].includes(value)
    },
    darkMode: {
      type: Boolean,
      default: false
    },
    fallbackIcon: {
      type: String,
      default: 'mdi-domain'
    }
  },
  
  data() {
    return {
      hasError: false,
      showFallback: false
    };
  },
  
  computed: {
    logoUrl() {
      if (!this.domain || this.hasError) {
        return null;
      }
      
      // Extract domain from URL if full URL is provided
      const cleanDomain = this.extractDomain(this.domain);
      
      if (!cleanDomain) {
        return null;
      }
      
      // Build Logo.dev URL
      const baseUrl = 'https://img.logo.dev';
      const apiKey = this.getApiKey();
      const theme = this.darkMode ? 'dark' : 'light';
      const format = 'webp'; // Prefer WebP for better performance
      
      return `${baseUrl}/${cleanDomain}?token=${apiKey}&theme=${theme}&format=${format}`;
    },
    
    logoSize() {
      const sizes = {
        small: '32',
        medium: '48',
        large: '64'
      };
      return sizes[this.size] || sizes.medium;
    }
  },
  
  methods: {
    extractDomain(url) {
      if (!url) return null;
      
      try {
        // If it's already a domain (no protocol), return as is
        if (!url.includes('://')) {
          return url.replace(/^www\./, '').split('/')[0];
        }
        
        // Parse URL and extract domain
        const urlObj = new URL(url);
        return urlObj.hostname.replace(/^www\./, '');
      } catch (e) {
        // If URL parsing fails, try to extract domain manually
        const match = url.match(/(?:https?:\/\/)?(?:www\.)?([^\/]+)/);
        return match ? match[1] : null;
      }
    },
    
    getApiKey() {
      // Get API key from environment or config
      // You should store this in your .env file or config
      return process.env.VUE_APP_LOGO_DEV_API_KEY || '';
    },
    
    handleImageError() {
      this.hasError = true;
      this.showFallback = true;
    },
    
    handleImageLoad() {
      this.hasError = false;
      this.showFallback = false;
    }
  },
  
  watch: {
    domain() {
      // Reset error state when domain changes
      this.hasError = false;
      this.showFallback = false;
    }
  }
};
</script>

<style scoped>
.company-logo-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
}

.company-logo {
  max-width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
}

.company-logo.logo-failed {
  display: none;
}

.logo-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

/* Size variants */
.logo-small {
  width: 32px;
  height: 32px;
}

.logo-medium {
  width: 48px;
  height: 48px;
}

.logo-large {
  width: 64px;
  height: 64px;
}

.company-logo-wrapper.logo-small .logo-fallback {
  width: 32px;
  height: 32px;
}

.company-logo-wrapper.logo-medium .logo-fallback {
  width: 48px;
  height: 48px;
}

.company-logo-wrapper.logo-large .logo-fallback {
  width: 64px;
  height: 64px;
}
</style>

